import React, { ForwardedRef, forwardRef, memo, useEffect, useReducer, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  EDDataGrid,
  EDIcon,
  EDIconSize,
  EDPlayerAvatar,
  EDTextField,
  EDTypography,
  error,
  grey,
  primary,
  spacing,
  success
} from "@trailblazer-game/ed-webkit";
import { styled } from "@mui/material/styles";
import { theme } from "../../../Theme/Theme";
import { truncateString } from "../../../../helpers/utils/string";
import { SessionContext, SessionContextInterface } from "../../../../hooks/SessionHook/SessionContext";
import { getTimestampDateFormatted } from "../../../../helpers/dates";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ILeagueNFTPrize, ILeagueRank } from "../../../../helpers/interfaces/league";
import { formatCurrency } from "../../../../helpers/numbers";
import ReplayIcon from '@mui/icons-material/Replay';
import {
  ELeagueLeaderboardActions,
  leagueLeaderboardInitialState,
  leagueLeaderboardReducer
} from "./League_LeaderboardState";
import { IWithLazyLoadProps, withLazyLoad } from "../../../../hooks/withLazyLoad";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from "@mui/material/Box";

interface ILeagueLeaderboardProps extends IWithLazyLoadProps {
  className?: string;
  reloadRanks: Function;
  ranks: ILeagueRank[];
}

const ELEMENTS_PER_PAGE = 10;
const REFRESH_TIMEOUT = 60 * 1000;

function notEmptyCell(params: any) {
  return (
    <EDTypography variant={"body2"}>{params.row[params.colDef.field] || "-"}</EDTypography>
  );
}

function notEmptyNumericCell(params: any) {
  return (
    <EDTypography variant={"body2"}>{params.row[params.colDef.field] || "0"}</EDTypography>
  );
}

function renderWins(params: any) {
  const { wins } = params.row;

  return <Stack className={"wins"} alignItems={"center"} justifyContent={"center"}>
    <EDTypography align={"center"} variant={wins > 999 ? "caption" : "body2"} sx={{ color: success("main") }}>{wins}</EDTypography>
  </Stack>
}

function getRankIcon(rank: number) {
  switch (rank) {
    case 1:
      return "league/Icon=Gold.png";
    case 2:
      return "league/Icon=Silver.png";
    case 3:
      return "league/Icon=Bronze.png";
    default:
      return "";
  }
}

const LeagueLeaderboard = memo(forwardRef(({
                                             className,
                                             reloadRanks,
                                             ranks
                                           }: ILeagueLeaderboardProps, ref: ForwardedRef<HTMLDivElement>) => {
  const [ state, dispatch ] = useReducer(leagueLeaderboardReducer, leagueLeaderboardInitialState);
  const [ nameFilter, setNameFilter ] = useState("");
  const { tbUid } = React.useContext(SessionContext) as SessionContextInterface;
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const COLUMNS = [
    {
      field: 'rank', headerName: '#', width: 40, sortable: false, renderCell: (params: any) => {
        const value = params.row.rank;
        const isTop3 = value <= 3;
        const isPlayerRow = params.row.tbUid == tbUid;
        return (
          <EDTypography
            align={"center"}
            sx={{ color: isTop3 ? primary("main") : isPlayerRow ? grey("1000") : grey("50") }}
            variant={isTop3 ? "h3" : "h6"}>
            {value}
          </EDTypography>
        )
      }
    },
    {
      field: 'delta', headerName: 'Last #', width: 80, sortable: false, renderCell: (params: any) => {
        const value = params.row.delta || 0;
        const absValue = Math.abs(value);
        const icon = value == 0 ? <RemoveIcon/> : value > 0 ? <ArrowDropUpIcon/> : <ArrowDropDownIcon/>;
        const color = value == 0 ? grey("600") : value > 0 ? success("main") : error("main");
        return (
          <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
            <EDIcon icon={icon} sx={{ color }} size={EDIconSize.Medium}/>
            <EDTypography
              align={"center"}
              variant={"h6"}
              sx={{ color }}>
              {absValue > 0 ? absValue : null}
            </EDTypography>
          </Stack>
        )
      }
    },
    {
      field: 'nickname', headerName: 'Player', minWidth: 400, flex: 1, sortable: false,
      renderCell: (params: any) => {
        const isPlayerRow = params.row.tbUid == tbUid;
        return (
          <a href={`/u/${params.row.nickname}`} rel="noreferrer noopener"
             style={{ position: "relative" }}>
            {params.row.rank <= 3 && <EDIcon icon={getRankIcon(params.row.rank)} className={"winner-icon"}
                                             size={EDIconSize.Normal}/>}
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
              <EDPlayerAvatar avatarUrl={`${process.env.NEXT_PUBLIC_CDN_URL}/avatar/${params.id}`}
                              title={"Visit profile page"}/>
              <EDTypography variant={"body2"} title={params.row.nickname}
                            sx={{ color: isPlayerRow ? grey("1000") : primary("main") }}>
                <b>{truncateString(params.row.nickname || "", 40)}</b>
              </EDTypography>
            </Stack>
          </a>
        );
      },
    },
    {
      field: 'wins',
      headerName: 'Wins',
      flex: 0,
      minWidth: 75,
      sortable: false,
      renderCell: renderWins
    },
    {
      field: 'ratio', headerName: 'Win Rate', flex: 0, minWidth: 75, sortable: false, renderCell: (params: any) => {
        return (
          <EDTypography
            variant={"body2"}>{`${Math.floor(params.row[params.colDef.field] * 100)}%` || "0%"}</EDTypography>
        )
      }
    },
    { field: 'rating', headerName: 'Rating', flex: 1, sortable: false, renderCell: notEmptyCell },
    {
      field: 'earningExpected',
      headerName: 'Expected Rewards',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const { expectedEarnings } = params.row;

        if (!expectedEarnings || expectedEarnings.length == 0) {
          return "--";
        }

        const result: React.ReactNode[] = [];

        expectedEarnings!.forEach((earning: Partial<ILeagueNFTPrize>, index: number) => {
          const label = `${earning.name} x${formatCurrency(earning.amount || 0)}`;
          if ((earning.amount || 0) > 0) {
            result.push(<Stack direction={"row"} alignItems={"center"} justifyContent={"flex-start"}
                               key={`earning-${index}`} title={label} spacing={0.5}>
              {earning.image &&
                  <img src={earning.image} alt={"NFT Image"} style={{ width: '24px', height: '24px' }}/>}
              <EDTypography variant={"caption"}>
                {label}
              </EDTypography>
            </Stack>);
          }
        })

        return (
          <Stack direction={"column"} alignItems={"flex-start"} justifyContent={"center"}>
            {result}
          </Stack>
        )
      }
    },
  ];

  useEffect(() => {
    let dataInterval = setInterval(() => {
      reloadRanks();
    }, REFRESH_TIMEOUT);

    return () => clearInterval(dataInterval);
  }, []);

  useEffect(() => {
    dispatch({ type: ELeagueLeaderboardActions.SetRankings, ranks });
  }, [ ranks ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch({ type: ELeagueLeaderboardActions.SetNameFilter, filter: nameFilter });
    }, 250);

    return () => clearTimeout(timeout);
  }, [ nameFilter ])

  if (!ranks) {
    return null;
  }

  const onPageChange = (page: number) => {
    dispatch({ type: ELeagueLeaderboardActions.SetPage, page });
  }

  const getUserRank = () => {
    if (state.ranks.length <= ELEMENTS_PER_PAGE) {
      return null;
    }

    if (state.ranks[state.ranks.length - 1].tbUid == tbUid) {
      return null;
    }

    // check the player position only if it's not the last one and not in the top 10
    return state.ranks.find((r) => r.rank > ELEMENTS_PER_PAGE && r.tbUid == tbUid);
  }

  const searchPlayer = (value: string) => {
    setNameFilter(value);
  }

  const userRank = getUserRank();

  return (
    <Stack className={className} spacing={4} alignItems={"flex-start"} ref={ref}>
      <Stack direction={isSmall ? "column" : "row"} spacing={2} alignItems={"center"}
             justifyContent={isSmall ? "center" : "flex-start"} sx={{ width: "100%" }}>
        <EDTypography variant={"h3"} sx={{ color: primary("main") }}>
          LEADERBOARD
        </EDTypography>
        <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
          Last updated: {getTimestampDateFormatted(state.lastUpdated.getTime())}
        </EDTypography>
        <Stack direction={"row"} spacing={0.5} alignItems={"center"} justifyContent={"center"}
               onClick={() => reloadRanks()} sx={{ cursor: "pointer", color: grey("400") }}>
          <EDIcon icon={<ReplayIcon/>} size={EDIconSize.Small}/>
          <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
            REFRESH
          </EDTypography>
        </Stack>
      </Stack>

      <EDTextField placeholder={"Search player name"} onChange={(value) => searchPlayer(value)}/>

      <Stack alignItems={"center"} justifyContent={"center"} spacing={1} sx={{ width: '100%' }}>
        {userRank &&
            <>
                <EDDataGrid autoHeight={true} columns={COLUMNS} rows={[ userRank ]}
                            getRowId={(row) => row.tbUid}
                            disableColumnFilter={true}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                            disableHeader={false}
                            disablePagination={true}
                            getRowHeight={() => 'auto'}
                            sx={{
                              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: spacing(0.5) },
                              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: spacing(1) },
                              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: spacing(2) },
                            }}
                            getRowClassName={() => 'player-row'}/>
                <EDIcon icon={<MoreVertIcon/>} sx={{ color: grey("600") }}/>
            </>
        }

        <EDDataGrid columns={COLUMNS}
                    autoHeight={true}
                    rows={state.ranks}
                    getRowId={(row) => row.tbUid}
                    disableColumnFilter={true}
                    disableSelectionOnClick={true}
                    disableColumnMenu={true}
                    rowsPerPageOptions={[ 10, 25, 50 ]}
                    onPageChange={onPageChange}
                    disableHeader={!!userRank}
                    initialState={{
                      pagination: {
                        pageSize: ELEMENTS_PER_PAGE
                      }
                    }}
                    page={state.activePage}
                    getRowHeight={() => 'auto'}
                    sx={{
                      '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: spacing(0.5) },
                      '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: spacing(1) },
                      '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: spacing(2) },
                    }}
                    getRowClassName={(params) => {
                      const isPlayer = params.row.tbUid == tbUid;
                      const oddOrEven = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
                      const isTop3 = params.row.rank <= 3;
                      return `${oddOrEven} ${isPlayer ? 'player-row' : ''} ${isTop3 ? "top-3" : ""}`;
                    }}/>
      </Stack>
    </Stack>
  );
}));

LeagueLeaderboard.displayName = "LeagueLeaderboard";

const LeagueLeaderboardStyled = styled(LeagueLeaderboard)(() => ({
  width: '100%',
  position: "relative",
  zIndex: 10,
  ".winner-icon": {
    position: "absolute",
    top: "-4px",
    left: "-8px",
    zIndex: 10,
    margin: '0 !important'
  },
  ".top-3": {
    background: "linear-gradient(90deg, #263639 0%, #3D3D3D 33.85%)",
    ".wins": {
      backgroundColor: grey("900")
    }
  },
  "& .MuiDataGrid-root .MuiDataGrid-virtualScroller .MuiDataGrid-row.player-row": {
    backgroundColor: `${grey("300")} !important`,
    color: grey("1000"),
    "&.top-3": {
      background: `linear-gradient(90deg, #263639 0%, ${grey("200")} 33.85%)`
    }
  },
  ".wins": {
    backgroundColor: grey("1000"),
    padding: theme.spacing(1),
    color: success("main"),
    flexGrow: 1,
    borderRadius: spacing(0.25),
    maxWidth: "50px"
  },
  ".top-3 .matches-wins-losses": {
    backgroundColor: grey("900")
  }
}));

// Export
export default withLazyLoad(LeagueLeaderboardStyled);
